"use client";

import { Link } from "@tanstack/react-router";
import { useId, useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "@/components/animate-ui/icons/arrow-right";
import { Button } from "@/components/ui/button";
import { GoogleButton } from "@/components/ui/google-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useFormValidation } from "@/hooks/use-form-validation";
import { initiateGoogleLogin } from "@/lib/auth/google-sso";
import { signup } from "@/lib/auth/service";
import { captureFrontendError } from "@/lib/errors";
import { signupSchema } from "@/lib/validation/auth";

export function SignupForm() {
	const firstNameId = useId();
	const emailId = useId();
	const passwordId = useId();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [firstName, setFirstName] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Form validation
	const validation = useFormValidation(signupSchema);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		const formData = {
			email: email.trim(),
			password,
			firstName: firstName.trim(),
		};

		// Validate form data
		if (!validation.validate(formData)) {
			toast.error("Please fix the errors below");
			return;
		}

		setIsSubmitting(true);
		validation.clearErrors();

		try {
			const result = await signup(formData);

			if (!result.success) {
				// Set field-level errors from API (422 responses)
				if (result.error.fieldErrors) {
					validation.setServerErrors(result.error.fieldErrors);
				}

				// Show toast for general error
				toast.error(result.error.message);
				return;
			}

			toast.success("Account created successfully! Redirecting...");
			// Auth state handled inside signup; redirect runs via useRedirectIfAuthenticated hook
		} catch (error) {
			captureFrontendError(error, { area: "auth", action: "signup_submit" });
			toast.error("An unexpected error occurred. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleGoogleSignup = async () => {
		try {
			setIsSubmitting(true);
			await initiateGoogleLogin();
			// User will be redirected to Google
		} catch (error) {
			captureFrontendError(error, { area: "auth", action: "signup_google_start" });
			toast.error("Failed to start Google sign-up. Please try again.");
			setIsSubmitting(false);
		}
	};

	return (
		<div className="flex flex-col gap-6">
			<div className="flex flex-col gap-2 text-center">
				<h1 className="text-2xl font-semibold tracking-tight">Create an account</h1>
				<p className="text-sm text-muted-foreground">Get started for free in under a minute</p>
			</div>

			{/* Google OAuth */}
			<GoogleButton onClick={handleGoogleSignup} disabled={isSubmitting}>
				Sign up with Google
			</GoogleButton>

			{/* Divider */}
			<div className="relative">
				<div className="absolute inset-0 flex items-center">
					<span className="w-full border-t" />
				</div>
				<div className="relative flex justify-center text-xs uppercase">
					<span className="bg-background px-2 text-muted-foreground">Or continue with email</span>
				</div>
			</div>

			<form onSubmit={handleSubmit} className="flex flex-col gap-6">
				<div className="flex flex-col gap-4">
					<div className="flex flex-col gap-2">
						<Label htmlFor={firstNameId}>First name</Label>
						<Input
							id={firstNameId}
							type="text"
							placeholder="John"
							value={firstName}
							onChange={(e) => setFirstName(e.target.value)}
							onBlur={() =>
								validation.validateField("firstName", firstName, {
									email,
									password,
									firstName,
								})
							}
							autoComplete="given-name"
							disabled={isSubmitting}
							required
						/>
						{validation.getFieldError("firstName") && (
							<p className="text-xs text-destructive">{validation.getFieldError("firstName")}</p>
						)}
					</div>

					<div className="flex flex-col gap-2">
						<Label htmlFor={emailId}>Email</Label>
						<Input
							id={emailId}
							type="email"
							placeholder="name@example.com"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							onBlur={() =>
								validation.validateField("email", email, {
									email,
									password,
									firstName,
								})
							}
							required
							autoComplete="email"
							disabled={isSubmitting}
						/>
						{validation.getFieldError("email") && (
							<p className="text-xs text-destructive">{validation.getFieldError("email")}</p>
						)}
					</div>

					<div className="flex flex-col gap-2">
						<Label htmlFor={passwordId}>Password</Label>
						<Input
							id={passwordId}
							type="password"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							onBlur={() =>
								validation.validateField("password", password, {
									email,
									password,
									firstName,
								})
							}
							required
							autoComplete="new-password"
							disabled={isSubmitting}
						/>
						{validation.getFieldError("password") && (
							<p className="text-xs text-destructive">{validation.getFieldError("password")}</p>
						)}
					</div>
				</div>

				<Button
					type="submit"
					disabled={!email.trim() || !password.trim() || !firstName.trim() || isSubmitting}
					className="w-full"
				>
					{isSubmitting ? (
						"Creating account..."
					) : (
						<>
							Create account
							<ArrowRight className="ml-2 size-4" animateOnHover />
						</>
					)}
				</Button>
			</form>

			<div className="text-center text-sm">
				<Link to="/login" className="text-muted-foreground underline-offset-4 hover:underline">
					Already have an account? Sign in
				</Link>
			</div>
		</div>
	);
}
